<template>
  <div class="print-page">
    <div class="print-toolbar no-print">
      <v-btn to="/iabf/admin/borrowed-tutus" small>&lt; Back to Borrowed Tutus</v-btn>
      <v-btn small color="primary" class="ml-2" @click="doPrint">🖨 Print</v-btn>
      <label class="filter-label ml-3">
        <input type="checkbox" v-model="hideUnassigned" /> Hide unassigned
      </label>
      <span class="ml-3 muted">{{ filteredItems.length }} of {{ allItems.length }} tutus</span>
    </div>

    <h1>IABF {{ adminFestivalYear }} Borrowed Tutus</h1>
    <p class="subtitle">B = bust, W = waist, H = hip, ½G = half-girth, G = girth, Ht = height. Body measurements in inches; height in feet'inches".</p>

    <section v-for="group in groupedItems" :key="group.type" class="type-group">
      <h2>{{ group.type }} <span class="count">({{ group.items.length }})</span></h2>
      <table class="inv-table">
        <thead>
          <tr>
            <th class="borrower-col">Borrowed by ({{ adminFestivalYear }})</th>
            <th class="name-col">Name on Tutu</th>
            <th>Measurements</th>
            <th class="returned-col">Returned</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in group.items" :key="item.id" :class="{ 'is-assigned': item.borrower }">
            <td class="borrower">
              <span v-if="item.borrower">
                <strong>{{ item.borrower.dancer }}</strong>
                <span class="role-tag">{{ item.borrower.role }}</span>
              </span>
              <span v-else class="muted">—</span>
            </td>
            <td class="name">
              {{ displayTutuName(item.name) }}
              <span v-if="item.color" class="color-tag">{{ item.color }}</span>
            </td>
            <td class="measurements">{{ formatMeasurements(item) }}</td>
            <td class="returned"><span class="check-box"></span></td>
          </tr>
        </tbody>
      </table>
    </section>

    <p v-if="!allItems.length" class="muted">No inventory loaded.</p>
  </div>
</template>

<script>
import iabfAdminMixin from '@/utils/iabf-admin-mixin'

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      hideUnassigned: false
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.$store.dispatch('loadCostumeInventory')
    this.$store.dispatch('loadIABFDancers')
  },
  computed: {
    inventory () {
      return this.$store.getters.costumeInventory || []
    },
    dancers () {
      return this.$store.getters.loadedIABFDancers || []
    },
    borrowerByTutuName () {
      // Maps tutu name -> { dancer, role } for the 2026 borrower of each tutu.
      const map = new Map()
      const addBorrower = (tutuName, dancer, role) => {
        if (!tutuName) return
        const name = `${dancer.firstName || ''} ${dancer.lastName ? dancer.lastName.charAt(0) + '.' : ''}`.trim()
        map.set(tutuName, { dancer: name, role })
      }
      for (const d of this.dancers) {
        addBorrower(d.borrowedWaltzTutu, d, 'Waltz of the Hours')
        addBorrower(d.borrowedAliceTutu, d, 'Alice in Wonderland')
        addBorrower(d.borrowedGarlandTutu, d, 'Garland Waltz')
      }
      return map
    },
    allItems () {
      return this.inventory.map(item => ({
        ...item,
        borrower: this.borrowerByTutuName.get(item.name) || null
      }))
    },
    filteredItems () {
      if (!this.hideUnassigned) return this.allItems
      return this.allItems.filter(i => i.borrower)
    },
    groupedItems () {
      const groups = new Map()
      for (const item of this.filteredItems) {
        const t = item.type || 'Other'
        if (!groups.has(t)) groups.set(t, [])
        groups.get(t).push(item)
      }
      // Order types in a consistent way; unknown types fall to the end.
      const typeOrder = ['Pancake', 'Pancake with Bodice', 'Romantic']
      const sortedTypes = Array.from(groups.keys()).sort((a, b) => {
        const ai = typeOrder.indexOf(a)
        const bi = typeOrder.indexOf(b)
        if (ai === -1 && bi === -1) return a.localeCompare(b)
        if (ai === -1) return 1
        if (bi === -1) return -1
        return ai - bi
      })
      return sortedTypes.map(type => ({
        type,
        items: groups.get(type).slice().sort((x, y) => {
          // Assigned tutus first, then by name.
          if (!!x.borrower !== !!y.borrower) return x.borrower ? -1 : 1
          return (x.name || '').localeCompare(y.name || '')
        })
      }))
    }
  },
  methods: {
    doPrint () {
      window.print()
    },
    displayTutuName (name) {
      // The "Buy New" flow defaults the inventory label to "Dancer (Borrow)".
      // Strip that suffix for the printed list so the tutu is just identified
      // by the borrower's name.
      return String(name || '').replace(/\s*\(Borrow\)\s*$/i, '')
    },
    formatHeight (val) {
      const n = parseFloat(val)
      if (!isFinite(n) || n <= 0) return ''
      const ft = Math.floor(n / 12)
      const inch = Math.round(n - ft * 12)
      // Roll 12" up to the next foot to avoid showing 5'12".
      if (inch === 12) return `${ft + 1}'0"`
      return `${ft}'${inch}"`
    },
    formatMeasurements (item) {
      const dims = [
        { label: 'B', val: item.bust, fmt: v => `${v}"` },
        { label: 'W', val: item.waist, fmt: v => `${v}"` },
        { label: 'H', val: item.hip, fmt: v => `${v}"` },
        { label: '½G', val: item.halfGirth, fmt: v => `${v}"` },
        { label: 'G', val: item.girth, fmt: v => `${v}"` },
        { label: 'Ht', val: item.height, fmt: v => this.formatHeight(v) }
      ]
      return dims
        .filter(d => d.val !== null && d.val !== undefined && d.val !== '' && d.fmt(d.val))
        .map(d => `${d.label} ${d.fmt(d.val)}`)
        .join(' · ')
    }
  }
}
</script>

<style scoped>
.print-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  color: #1a1a1a;
}

.print-toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
}

.filter-label {
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.muted {
  color: #999;
  font-style: italic;
}

h1 {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 700;
  font-size: 30px;
  margin: 0 0 4px;
}

.subtitle {
  color: #555;
  font-size: 13px;
  margin: 0 0 18px;
}

.type-group {
  margin-bottom: 24px;
  page-break-inside: avoid;
}

h2 {
  font-family: Quattrocento, Georgia, serif;
  font-size: 20px;
  font-weight: 700;
  border-bottom: 2px solid #267E7D;
  padding-bottom: 4px;
  margin: 0 0 8px;
}

h2 .count {
  font-size: 14px;
  font-weight: 400;
  color: #888;
  margin-left: 6px;
}

.inv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.inv-table th,
.inv-table td {
  text-align: left;
  padding: 6px 8px;
  border-bottom: 1px solid #e6e1d4;
  vertical-align: top;
}

.inv-table th {
  font-family: Quattrocento, Georgia, serif;
  font-size: 12px;
  letter-spacing: 0.04em;
  background: #f7f5ef;
}

.inv-table .name {
  font-weight: 700;
}

.inv-table tr.is-assigned {
  background: #f0f8f3;
}

.borrower-col {
  width: 200px;
}

.name-col {
  width: 220px;
}

.borrower strong {
  display: inline-block;
}

.role-tag {
  display: block;
  font-size: 11px;
  color: #555;
  font-style: italic;
}

.color-tag {
  display: block;
  font-size: 11px;
  color: #555;
  font-style: italic;
}

.measurements {
  font-family: 'Fanwood Text', Georgia, serif;
  letter-spacing: 0.02em;
}

.returned-col {
  width: 80px;
  text-align: center;
}

.returned {
  text-align: center;
}

.check-box {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 1.5px solid #333;
  border-radius: 3px;
}

@media print {
  .no-print {
    display: none !important;
  }
  .print-page {
    padding: 0;
    max-width: none;
  }
  h1 {
    font-size: 22px;
  }
  h2 {
    font-size: 16px;
  }
  .inv-table {
    font-size: 11px;
  }
  .inv-table th,
  .inv-table td {
    padding: 3px 5px;
  }
  .type-group {
    margin-bottom: 14px;
  }
  /* Force light background regardless of dark-mode UA defaults */
  body, .print-page {
    background: #fff;
    color: #000;
  }
}
</style>
</content>
</invoke>